import React from "react";
import {
    Menu,
    Video,
    CreditCard,
    Settings,
    LogOut,
    User,
    Bell,
    X,
    ChevronLeft,
    ChevronRight,
    Shield,
    Sparkles,
    LayoutGrid,
    Clock,
    Zap,
    Users as UsersIcon,
    Download,
    CheckCircle,
    AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "../../components/ui/avatar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../components/ui/popover";
import { useNotifications, Notification } from "../../contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { downloadFile } from "../../utils/file-utils";
import { motion, AnimatePresence } from "framer-motion";

// Types
export interface Project {
    id: string;
    title: string;
    thumbnailUrl?: string;
    previewUrl?: string;
    editLink?: string;
    createdAt: string;
    updatedAt: string;
}

export interface UserType {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    storageUsed?: number;
}

// Notification Item Component
interface NotificationItemProps {
    notification: Notification;
    onDismiss: () => void;
    onRead: () => void;
}

export const NotificationItem = ({
    notification,
    onDismiss,
    onRead,
}: NotificationItemProps) => {
    const { title, message, createdAt, read, data } = notification;

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (data?.outputUrl) {
            const filename = `video-${data.exportId || 'export'}.mp4`;
            await downloadFile(data.outputUrl, filename);
        }
    };

    const getIcon = () => {
        if (data?.type === 'export_status' || notification.type === 'export_status') {
            switch (data?.status) {
                case 'completed': return <CheckCircle className="h-5 w-5 text-emerald-500" />;
                case 'failed': return <AlertTriangle className="h-5 w-5 text-rose-500" />;
                case 'processing': return <Clock className="h-5 w-5 text-indigo-500 animate-pulse" />;
                default: return <Bell className="h-5 w-5 text-indigo-500" />;
            }
        }
        return <Bell className="h-5 w-5 text-slate-400" />;
    };

    return (
        <div
            className={`flex items-start space-x-3 p-3 hover:bg-slate-50 transition-all duration-200 cursor-pointer rounded-xl mb-1 ${!read ? 'bg-indigo-50/50' : ''}`}
            onClick={onRead}
        >
            <div className="mt-1 flex-shrink-0">{getIcon()}</div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm font-semibold ${!read ? 'text-slate-900' : 'text-slate-600'}`}>{title}</p>
                    <p className="text-[10px] text-slate-400 font-medium">{new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <p className="text-xs text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">{message}</p>

                {data?.status === 'completed' && data?.outputUrl && (
                    <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 bg-slate-100 hover:bg-slate-200 text-slate-700 mt-2 flex items-center gap-2 rounded-lg border border-slate-200 w-full justify-center group"
                        onClick={handleDownload}
                    >
                        <Download className="h-3.5 w-3.5 group-hover:translate-y-0.5 transition-transform" />
                        Download Video
                    </Button>
                )}
            </div>
            <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-400 hover:text-slate-900 hover:bg-slate-100"
                onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
};

// Toast Container Component
export const ToastContainer = () => {
    const { toasts, removeToast, markAsRead } = useNotifications();

    return (
        <div className="fixed bottom-6 right-6 z-[100] flex flex-col space-y-3 max-w-sm w-full outline-none">
            <AnimatePresence>
                {toasts.map((notification) => (
                    <motion.div
                        key={notification._id}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                        layout
                        className="bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden pointer-events-auto"
                    >
                        <NotificationItem
                            notification={notification}
                            onRead={() => {
                                markAsRead(notification._id);
                                removeToast(notification._id);
                            }}
                            onDismiss={() => removeToast(notification._id)}
                        />
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};

// Project Card Component
interface ProjectCardProps {
    project: Project;
    onOpen: (id: string) => void;
    onDelete: (id: string) => void;
}

export const ProjectCard = ({ project, onOpen, onDelete }: ProjectCardProps) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) return `${diffDays} days ago`;
        return `${Math.floor(diffDays / 7)}w ago`;
    };

    return (
        <motion.div
            whileHover={{ y: -4 }}
            className="group block bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer flex flex-col h-full"
            onClick={() => onOpen(project.id)}
        >
            {/* Image Section */}
            <div className="relative aspect-video overflow-hidden bg-slate-100">
                <img
                    src={
                        (project.thumbnailUrl && project.thumbnailUrl !== "") ?
                            project.thumbnailUrl :
                            `https://picsum.photos/400/225?random=${project.id}`
                    }
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />

                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Button variant="secondary" className="bg-white text-slate-900 border-none shadow-xl rounded-full px-6 flex items-center gap-2">
                        Open Studio
                    </Button>
                </div>

                {/* Delete button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(project.id);
                    }}
                    className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm text-slate-400 hover:text-rose-500 transition-all rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center shadow-sm"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            {/* Info Section */}
            <div className="p-4 flex-1 flex flex-col">
                <h3 className="text-sm font-bold text-slate-900 line-clamp-1 group-hover:text-indigo-600 transition-colors mb-2">
                    {project.title}
                </h3>
                <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-1.5 text-slate-400 text-[11px] font-medium">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatDate(project.updatedAt)}</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// Sidebar Component
interface SidebarProps {
    isOpen: boolean;
    isMobile: boolean;
    onToggle: () => void;
    user?: UserType | null;
    activePage?: 'dashboard' | 'my-videos' | 'billing' | 'admin' | 'admin-users' | 'admin-projects' | 'profile' | 'settings';
}

export const Sidebar = ({ isOpen, isMobile, onToggle, user, activePage = 'dashboard' }: SidebarProps) => {
    const navigate = useNavigate();

    const menuItems = [
        { label: 'My Projects', icon: LayoutGrid, id: 'dashboard', path: '/dashboard' },
        { label: 'My Videos', icon: Video, id: 'my-videos', path: '/my-videos' },
        { label: 'Billing', icon: CreditCard, id: 'billing', path: '/billing' },
    ];

    return (
        <aside
            className={`
                ${isMobile
                    ? isOpen ? "translate-x-0" : "-translate-x-full"
                    : "translate-x-0"
                }
                ${isMobile ? "fixed inset-y-0 left-0 z-50" : "relative"}
                w-72 bg-white border-r border-slate-200 transition-all duration-300 ease-in-out
                flex flex-col shadow-sm
            `}
        >
            <div className="flex items-center justify-between h-20 px-8 border-b border-slate-100">
                <div
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => navigate('/dashboard')}
                >
                    <img src="/vidlaya-logo.png" className="w-10 h-10 object-contain" alt="VidLaya" />
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                        VidLaya
                    </h2>
                </div>
                {isMobile && (
                    <Button variant="ghost" size="icon" onClick={onToggle} className="text-slate-400">
                        <X className="h-5 w-5" />
                    </Button>
                )}
            </div>

            <div className="flex-1 px-4 py-8 overflow-y-auto custom-scrollbar">
                <div className="space-y-1">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activePage === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => navigate(item.path)}
                                className={`
                                    w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group
                                    ${isActive
                                        ? 'bg-indigo-50 text-indigo-700 font-semibold'
                                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                                `}
                            >
                                <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'} transition-colors`} />
                                <span className="text-sm">{item.label}</span>
                                {isActive && (
                                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-600" />
                                )}
                            </button>
                        );
                    })}
                </div>

                {user && typeof user.storageUsed === 'number' && (
                    <div className="mt-8 px-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Storage</span>
                            <span className="text-[10px] font-bold text-slate-400">{((user.storageUsed || 0) / (1024 * 1024 * 1024)).toFixed(2)} GB / 30 GB</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                            <div 
                                className={`h-2 rounded-full ${user.storageUsed / (30 * 1024 * 1024 * 1024) > 0.9 ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.3)]' : 'bg-indigo-500 hover:opacity-90'}`} 
                                style={{ width: `${Math.min(100, (user.storageUsed / (30 * 1024 * 1024 * 1024)) * 100)}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {localStorage.getItem('user_role') === 'admin' && (
                    <div className="mt-8">
                        <p className="px-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-4">Management</p>
                        <div className="space-y-1">
                            <button
                                onClick={() => navigate('/admin')}
                                className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${activePage === 'admin' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'} group`}
                            >
                                <Shield className={`mr-3 h-5 w-5 ${activePage === 'admin' ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                                <span className="text-sm">Admin Overview</span>
                            </button>
                            <button
                                onClick={() => navigate('/admin/users')}
                                className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 ${activePage === 'admin-users' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'} group`}
                            >
                                <UsersIcon className={`mr-3 h-5 w-5 ${activePage === 'admin-users' ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                                <span className="text-sm">Users</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </aside>
    );
};

// Header Component
interface HeaderProps {
    isMobile: boolean;
    onToggleSidebar: () => void;
    user: UserType | null;
    onLogout: () => void;
    title?: string;
}

export const Header = ({
    isMobile,
    onToggleSidebar,
    user,
    onLogout,
    title,
}: HeaderProps) => {
    const { unreadCount, markAsRead, deleteNotification, notifications } = useNotifications();
    const navigate = useNavigate();

    return (
        <header className="flex items-center justify-between h-20 px-8 bg-white border-b border-slate-200 sticky top-0 z-40">
            <div className="flex items-center gap-4">
                {isMobile && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onToggleSidebar}
                        className="text-slate-400 hover:text-slate-900"
                    >
                        <Menu className="h-6 w-6" />
                    </Button>
                )}
                <div className="flex flex-col">
                    <h1 className="text-lg font-bold text-slate-900 tracking-tight flex items-center gap-2">
                        {title || "Studio"}
                    </h1>
                </div>
            </div>

            <div className="flex items-center gap-3">
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="relative text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full w-10 h-10 transition-all border border-transparent"
                        >
                            <Bell className="h-5 w-5" />
                            {unreadCount > 0 && (
                                <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white ring-2 ring-white shadow-sm">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0 bg-white border-slate-200 shadow-2xl rounded-2xl overflow-hidden mt-2" align="end">
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white">
                            <h4 className="text-sm font-bold text-slate-900">Notifications</h4>
                            <span className="text-[10px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-bold">{unreadCount} New</span>
                        </div>
                        <div className="max-h-[70vh] overflow-auto custom-scrollbar p-1">
                            {notifications && notifications.length > 0 ? (
                                notifications.map((notification) => (
                                    <NotificationItem
                                        key={notification._id}
                                        notification={notification}
                                        onRead={() => markAsRead(notification._id)}
                                        onDismiss={() => deleteNotification(notification._id)}
                                    />
                                ))
                            ) : (
                                <div className="text-center py-12">
                                    <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Bell className="w-6 h-6 text-slate-300" />
                                    </div>
                                    <p className="text-sm font-medium text-slate-400">All caught up!</p>
                                    <p className="text-[11px] text-slate-400 mt-1">No new notifications detected.</p>
                                </div>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>

                <div className="h-8 w-[1px] bg-slate-200 mx-1"></div>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="relative h-11 px-2 rounded-full hover:bg-slate-100 transition-all flex items-center gap-3 group">
                            <span className="hidden md:block text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">{user?.name?.split(' ')[0]}</span>
                            <Avatar className="h-9 w-9 border-2 border-slate-100 shadow-sm transition-transform group-hover:scale-105">
                                <AvatarImage src={user?.avatar || "/api/placeholder/40/40"} alt={user?.name} />
                                <AvatarFallback className="bg-indigo-600 text-white font-bold text-sm">
                                    {user?.name?.charAt(0).toUpperCase() || "U"}
                                </AvatarFallback>
                            </Avatar>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        sideOffset={8}
                        className="w-64 bg-white border border-slate-200 text-slate-900 rounded-2xl shadow-2xl p-2"
                    >
                        <DropdownMenuLabel className="p-3">
                            <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                    <AvatarFallback className="bg-slate-100 text-slate-600 font-bold">
                                        {user?.name?.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col">
                                    <p className="text-sm font-bold text-slate-900">{user?.name || "Member"}</p>
                                    <p className="text-[11px] text-slate-500 font-medium truncate max-w-[140px]">
                                        {user?.email || "No email available"}
                                    </p>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator className="bg-slate-100 mx-2" />
                        <div className="p-1 space-y-1">
                            <DropdownMenuItem onClick={() => navigate('/profile')} className="rounded-xl px-3 py-2 cursor-pointer focus:bg-slate-50 focus:text-indigo-600 group">
                                <User className="mr-3 h-4.5 w-4.5 text-slate-400 group-focus:text-indigo-600" />
                                <span className="text-sm font-medium">Profile</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate('/settings')} className="rounded-xl px-3 py-2 cursor-pointer focus:bg-slate-50 focus:text-indigo-600 group">
                                <Settings className="mr-3 h-4.5 w-4.5 text-slate-400 group-focus:text-indigo-600" />
                                <span className="text-sm font-medium">Settings</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-100 mx-2" />
                            <DropdownMenuItem onClick={onLogout} className="rounded-xl px-3 py-2 cursor-pointer focus:bg-rose-50 focus:text-rose-600 group">
                                <LogOut className="mr-3 h-4.5 w-4.5 text-slate-400 group-focus:text-rose-600" />
                                <span className="text-sm font-medium">Log out</span>
                            </DropdownMenuItem>
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
};

// Pagination Component
interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <div className="flex items-center justify-center space-x-2 mt-8 pb-8">
            <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="bg-white border-slate-200 text-slate-500 hover:text-slate-900 rounded-xl w-9 h-9 shadow-sm"
            >
                <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1.5 p-1 bg-white border border-slate-200 rounded-2xl shadow-sm">
                {startPage > 1 && (
                    <>
                        <Button
                            variant="ghost"
                            onClick={() => onPageChange(1)}
                            className="h-8 w-8 px-0 text-slate-600 hover:text-indigo-600 rounded-lg text-xs font-semibold"
                        >
                            1
                        </Button>
                        {startPage > 2 && <span className="text-slate-300 px-1 select-none text-xs">···</span>}
                    </>
                )}

                {pages.map((page) => (
                    <Button
                        key={page}
                        variant="ghost"
                        onClick={() => onPageChange(page)}
                        className={`
                            h-8 w-8 px-0 rounded-lg text-xs font-bold transition-all duration-200
                            ${currentPage === page
                                ? "bg-indigo-600 text-white shadow-md shadow-indigo-100"
                                : "text-slate-500 hover:text-indigo-600 hover:bg-slate-50"}
                        `}
                    >
                        {page}
                    </Button>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <span className="text-slate-300 px-1 select-none text-xs">···</span>}
                        <Button
                            variant="ghost"
                            onClick={() => onPageChange(totalPages)}
                            className="h-8 w-8 px-0 text-slate-600 hover:text-indigo-600 rounded-lg text-xs font-semibold"
                        >
                            {totalPages}
                        </Button>
                    </>
                )}
            </div>

            <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="bg-white border-slate-200 text-slate-500 hover:text-slate-900 rounded-xl w-9 h-9 shadow-sm"
            >
                <ChevronRight className="h-4 w-4" />
            </Button>
        </div>
    );
};
